import { trackPromise } from 'react-promise-tracker';

import { API_PATHS, APIClients, interpolatePathParams, REQUEST_METHODS, ROUTE_PARAMS } from '@futrli/shared-services';
import {
  ExcelForecastTemplateGenerationRequest,
  ExcelForecastTemplateGenerationResponse,
  ExcelForecastUploadResponse,
  ExcelForecastValidationFailureResponse,
  ExcelForecastValidationSuccessResponse,
  Forecast,
  ForecastListItem,
  ThisNumberExplainedRow,
} from '@futrli-sdk/predict';
import { LOADER_AREAS } from '@futrli/ui-components';

import { BulkUpdatedPredictionsPayload } from '../../ui/assistant-side-panel/bulk-update/types';
import {
  ConsolidatedForecastReport,
  DataCheckResponse,
  ForecastReplacePayload,
  QueryParams
} from '../types';
import {
  CreateNewForecastPayload,
  ExcelForecastCreatePayload,
  ForecastUpdatePayload
} from '../../forecast/dashboard/types';
import { makeRequestWithResponse } from './shared';

/**
 * Creates new budgets or scenario
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {QueryParams} queryParams
 * @returns {Promise} - Returns a new or existing forecast
 * @throws error
 */
export const createNewForecast = async (
  workspaceId: string,
  organisationId: string,
  data: CreateNewForecastPayload,
  queryParams: QueryParams = {}
): Promise<Forecast> => {
  const path = interpolatePathParams(API_PATHS.FETCH_MULTIPLE_FORECAST, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<Forecast>({
    data: { data },
    params: queryParams,
    url: path,
    method: REQUEST_METHODS.POST
  });
};

/**
 * Replaces forecast
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId
 * @returns {Promise} - Returns updated forecast
 * @throws error
 */
export const replaceForecast = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  data: Partial<ForecastReplacePayload>
): Promise<Forecast> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<Forecast>({
    data: { data },
    url: path,
    method: REQUEST_METHODS.PUT
  });
};

/**
 * Gets all forecasts for an organisation
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {QueryParams} queryParams
 * @returns {Promise} - Returns a new or existing forecast
 * @throws error
 */
export const fetchMultipleForecasts = async (
  workspaceId: string,
  organisationId: string,
  queryParams: QueryParams = {}
): Promise<ForecastListItem[]> => {
  const path = interpolatePathParams(API_PATHS.FETCH_MULTIPLE_FORECAST, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<ForecastListItem[]>({ params: queryParams, url: path, method: REQUEST_METHODS.GET });
};

/**
 * Fetch forecast
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @param {string} forecastId - forecastId for the forecast to get
 * @param {QueryParams} queryParams
 * @returns {Promise<PlayReportDataWrapper>} - Returns forecast data
 * @throws error
 */
export const fetchForecastById = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  queryParams: QueryParams = {}
): Promise<Forecast> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<Forecast>({ params: queryParams, url: path });
};

/**
 * Fetch forecast report
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @param {string} forecastId - forecastId for the forecast to get
 * @param {QueryParams} queryParams
 * @returns
 * @throws error
 */
export const fetchForecastReportById = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  queryParams: QueryParams = {}
): Promise<ConsolidatedForecastReport> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_REPORT_BY_ID_V2, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return trackPromise(
    makeRequestWithResponse<ConsolidatedForecastReport>({ params: queryParams, url: path }),
    LOADER_AREAS.predictArea
  );
};

/**
 * Fetch forecast "this number explained" messages
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @param {string} forecastId - forecastId for the forecast to get
 */
export const fetchForecastThisNumberExplained = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string
) => {
  const route = API_PATHS.FORECAST_THIS_NUMBER_EXPLAINED;
  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return trackPromise(
    makeRequestWithResponse<{ rows: Record<string, ThisNumberExplainedRow> }>({ url: path }),
    LOADER_AREAS.predictArea
  );
};

/**
 * updates a forecast, using workspace, organaisation and forecast ID and a partial forecast
 *
 * @param {QueryParams} data
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - forecast to update
 * @param {QueryParams} queryParams
 * @returns {Promise} - Returns forecasts
 * @throws error
 */
export const updateForecast = async (
  data: ForecastUpdatePayload,
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  queryParams: QueryParams = {},
  loadingArea = LOADER_AREAS.predictArea
): Promise<Forecast> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return trackPromise(
    makeRequestWithResponse<Forecast>({ data, params: queryParams, url: path, method: REQUEST_METHODS.PATCH }),
    loadingArea
  );
};

export const bulkUpdateForecast = async (
  data: Partial<BulkUpdatedPredictionsPayload>,
  workspaceId: string,
  organisationId: string,
  forecastId: string
): Promise<void> => {
  const path = interpolatePathParams(API_PATHS.FORECASTS_BULK_UPDATE, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return trackPromise(
    makeRequestWithResponse<void>({ data, url: path, method: REQUEST_METHODS.PATCH }),
    LOADER_AREAS.predictArea
  );
};

export const deleteForecast = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string
): Promise<Forecast> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return trackPromise(
    makeRequestWithResponse<Forecast>({ url: path, method: REQUEST_METHODS.DELETE }),
    LOADER_AREAS.predictArea
  );
};

export const checkForData = async (workspaceId: string, organisationId: string): Promise<DataCheckResponse> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_DATA_CHECK, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<DataCheckResponse>({ url: path, method: REQUEST_METHODS.GET });
};

/**
 * Get excel file validation status
 *
 * @param {string} workspaceId - related workspace
 * @param {string} excelId - related excel ID
 * @returns {Promise<{ excel_id: string; validation_status: string; error_details: ExcelOrganisationValidationFailureResponse | null; }>} - Returns
 * @throws error
 */
export const getExcelFileValidation = async (
  workspaceId: string,
  organisationId: string,
  excelId: string,
  forecastId: string | null,
  forecast_from_date: string
): Promise<
  | ExcelForecastValidationFailureResponse
  | ExcelForecastValidationSuccessResponse
  | {
      excel_id: string;
      validation_status: string;
    }
> => {
  const url = interpolatePathParams(API_PATHS.EXCEL_FORECAST_FILE_VALIDATION, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.EXCEL_ID]: excelId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    validateStatus: () => true,
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        url,
        data: forecastId ? { forecast_id: forecastId, forecast_from_date } : { forecast_from_date }
      }
    }
  });
  return (response.data as unknown) as
    | ExcelForecastValidationFailureResponse
    | {
        excel_id: string;
        validation_status: string;
      };
};

/**
 * Generate protobuf file from excel file to populate forecast data
 *
 * @param {string} workspaceId - related workspace
 * @param {string} excelId - related excelId
 * @param {string} organisationId - related organisationId
 * @returns {Promise<ExcelForecastUploadResponse>}
 * @throws error
 */
export const generateForecast = async (
  workspaceId: string,
  organisationId: string,
  excelId: string,
  data: Partial<ExcelForecastCreatePayload>
): Promise<ExcelForecastUploadResponse> => {
  const url = interpolatePathParams(API_PATHS.EXCEL_FORECAST_UPLOAD, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.EXCEL_ID]: excelId
  });

  return makeRequestWithResponse<ExcelForecastUploadResponse>({
    url,
    method: REQUEST_METHODS.POST,
    data
  });
};

/**
 * Generate forecast excel template for given forecast kind and forecast from date
 *
 * @param {string} workspaceId - related workspace
 * @param {string} organisationId - related organisationId
 * @returns {Promise<ExcelForecastUploadResponse>}
 * @throws error
 */
export type ForecastTemplateGenerationPayload = Omit<
  ExcelForecastTemplateGenerationRequest,
  'window_from' | 'window_spec'
> &
  Partial<ExcelForecastTemplateGenerationRequest>;

export const generateForecastTemplate = async (
  workspaceId: string,
  organisationId: string,
  data: ForecastTemplateGenerationPayload
): Promise<ExcelForecastTemplateGenerationResponse> => {
  const url = interpolatePathParams(API_PATHS.GENERATE_FORECAST_TEMPLATE, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });

  return makeRequestWithResponse<ExcelForecastTemplateGenerationResponse>({
    url,
    method: REQUEST_METHODS.POST,
    data
  });
};
